import React, { useEffect, useState, useCallback } from 'react';
import {
    Row,
    Col,
    Form,
    Button,
    Table,
    Spinner,
    Alert,
    Modal,
    Badge,
} from 'react-bootstrap';
import {
    fetchClosedDays,
    createClosedDay,
    updateClosedDay,
    deleteClosedDay,
} from '../../../firebase-config';

/**
 * 休業日管理ページ.
 */
function HolidayClosedDaysPage({ myEmployee }) {
    const today = new Date();
    const thisYear = today.getFullYear();

    const [from, setFrom] = useState(`${thisYear}-01-01`);
    const [to, setTo] = useState(`${thisYear}-12-31`);

    const [items, setItems] = useState([]);

    const [loading, setLoading] = useState(false); // 一覧読み込み
    const [saving, setSaving] = useState(false);   // 登録/更新/削除

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // フォームモーダル
    const [showFormModal, setShowFormModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [form, setForm] = useState({
        id: null,
        date: '',
        name: '',
        kind: 'national',
    });

    // 削除モーダル
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    // 一括登録モーダル
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [bulkText, setBulkText] = useState('');
    const [bulkLoading, setBulkLoading] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        document.title = "休業日管理";
    }, []);

    const resetForm = () => {
        setForm({
            id: null,
            date: '',
            name: '',
            kind: 'national',
        });
        setIsEdit(false);
    };

    const loadClosedDays = useCallback(async () => {
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const data = await fetchClosedDays(from, to);
            setItems(data);
        } catch (e) {
            console.error(e);
            setError('休業日の取得に失敗しました。');
        } finally {
            setLoading(false);
        }
    }, [from, to]);

    useEffect(() => {
        loadClosedDays();
    }, [loadClosedDays]);

    const handleOpenNewModal = () => {
        resetForm();
        setError('');
        setSuccess('');
        setIsEdit(false);
        setShowFormModal(true);
    };

    const handleEditClick = (item) => {
        setError('');
        setSuccess('');
        setForm({
            id: item.id,
            date: item.date,
            name: item.name,
            kind: item.kind || 'national',
        });
        setIsEdit(true);
        setShowFormModal(true);
        window.scrollTo(0, 0);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setSaving(true);

        const payload = {
            date: form.date,
            name: form.name,
            kind: form.kind,
        };

        try {
            if (isEdit && form.id) {
                await updateClosedDay(form.id, payload);
                setSuccess('休業日を更新しました。');
            } else {
                await createClosedDay(payload);
                setSuccess('休業日を登録しました。');
            }
            setShowFormModal(false);
            resetForm();
            await loadClosedDays();
        } catch (e) {
            console.error(e);
            setError('保存に失敗しました。');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteClick = (item) => {
        setError('');
        setSuccess('');
        setDeleteTarget(item);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            await deleteClosedDay(deleteTarget.id);
            setSuccess('休業日を削除しました。');
            setShowDeleteModal(false);
            setDeleteTarget(null);
            await loadClosedDays();
        } catch (e) {
            console.error(e);
            setError('削除に失敗しました。');
        } finally {
            setSaving(false);
        }
    };

    const handleBulkImport = async () => {
        setError('');
        setSuccess('');
        setBulkLoading(true);

        try {
            const lines = bulkText
                .split('\n')
                .map((l) => l.trim())
                .filter((l) => l.length > 0);

            for (const line of lines) {
                // フォーマット: YYYY-MM-DD,名称[,kind]
                const [date, name, kindRaw] = line.split(',').map((v) => v.trim());
                if (!date || !name) {
                    console.warn('skip line:', line);
                    continue;
                }
                const kind = kindRaw || 'national'; // 省略時は祝日扱い

                await createClosedDay({ date, name, kind });
            }

            setSuccess('一括登録が完了しました。');
            setBulkText('');
            setShowBulkModal(false);
            await loadClosedDays();
        } catch (e) {
            console.error(e);
            setError('一括登録に失敗しました。');
        } finally {
            setBulkLoading(false);
        }
    };

    return (
        <Row className="mt-4">
            <h1>祝日管理</h1>

            {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
            {success && <Alert variant="success" className="mt-3">{success}</Alert>}

            {/* 期間フィルタ + 再読込 + 追加ボタン */}
            <Form
                className="mt-4 mb-3"
                onSubmit={(e) => {
                    e.preventDefault();
                    loadClosedDays();
                }}
            >
                <Row>
                    <Col md={3}>
                        <Form.Group>
                            <Form.Label>表示開始日</Form.Label>
                            <Form.Control
                                type="date"
                                value={from}
                                onChange={(e) => setFrom(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group>
                            <Form.Label>表示終了日</Form.Label>
                            <Form.Control
                                type="date"
                                value={to}
                                onChange={(e) => setTo(e.target.value)}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={2} className="d-flex flex-column align-items-stretch justify-content-end">
                        <Button
                            variant="primary"
                            type="button"
                            className="w-100"
                            onClick={handleOpenNewModal}
                        >
                            休業日を追加
                        </Button>
                    </Col>

                    <Col md={2} className="d-flex align-items-end">
                        <Button
                            variant="outline-primary"
                            type="button"
                            className="w-100 mt-2"
                            onClick={() => {
                                setError('');
                                setSuccess('');
                                setShowBulkModal(true);
                            }}
                        >
                            一括登録
                        </Button>
                    </Col>
                </Row>
            </Form>

            {/* 一覧 */}
            <Row className="mt-3">
                {loading && (
                    <div className="mt-3 mb-3">
                        <Spinner animation="border" variant="dark" />
                    </div>
                )}

                {!loading && items.length === 0 && (
                    <p className="mt-3">対象期間の休業日は登録されていません。</p>
                )}

                {!loading && items.length > 0 && (
                    <Table striped bordered hover className="mt-3">
                        <thead>
                            <tr>
                                <th>日付</th>
                                <th>名称</th>
                                <th>種別</th>
                                <th style={{ width: '160px' }}>操作</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item) => (
                                <tr key={item.id}>
                                    <td>{item.date}</td>
                                    <td>{item.name}</td>
                                    <td>{item.kind === 'national' ? '祝日' : '会社休日'}</td>
                                    <td>
                                        <Button
                                            variant="light"
                                            size="sm"
                                            className="me-2"
                                            onClick={() => handleEditClick(item)}
                                        >
                                            編集
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => handleDeleteClick(item)}
                                        >
                                            削除
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Row>

            <Alert variant="info" className="mt-3">
                <strong>祝日について</strong>
                <p>
                    <a href='ttps://www8.cao.go.jp/chosei/shukujitsu/gaiyou.html' target='_blank'>国民の祝日について - 内閣府</a>に記載の祝日を登録してください。
                    <br />
                    CSVをダウンロードしてきて登録するのが最も簡単です。
                </p>
            </Alert>

            {/* 追加/編集モーダル */}
            <Modal
                show={showFormModal}
                onHide={() => {
                    if (!saving) {
                        setShowFormModal(false);
                        resetForm();
                    }
                }}
            >
                <Form onSubmit={handleSubmit}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {isEdit ? '休業日の編集' : '休業日の追加'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group controlId="modal-holiday-date">
                            <Form.Label>※日付</Form.Label>
                            <Form.Control
                                type="date"
                                value={form.date}
                                onChange={(e) => setForm({ ...form, date: e.target.value })}
                                required
                            />
                        </Form.Group>

                        <Form.Group controlId="modal-holiday-name" className="mt-3">
                            <Form.Label>※名称</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="例）会社休日 / 振替休日"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                required
                            />
                        </Form.Group>

                        <Form.Group controlId="modal-holiday-kind" className="mt-3">
                            <Form.Label>種別</Form.Label>
                            <Form.Select
                                value={form.kind}
                                onChange={(e) => setForm({ ...form, kind: e.target.value })}
                            >
                                <option value="national">祝日</option>
                                <option value="company">会社休日</option>
                            </Form.Select>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={() => {
                                if (!saving) {
                                    setShowFormModal(false);
                                    resetForm();
                                }
                            }}
                        >
                            キャンセル
                        </Button>
                        <Button variant="primary" type="submit" disabled={saving}>
                            {isEdit ? '更新' : '登録'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* 削除確認モーダル */}
            <Modal
                show={showDeleteModal}
                onHide={() => {
                    if (!saving) {
                        setShowDeleteModal(false);
                        setDeleteTarget(null);
                    }
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title>休業日の削除確認</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {deleteTarget ? (
                        <>
                            <p>以下の休業日を削除しますか？</p>
                            <ul>
                                <li>日付：{deleteTarget.date}</li>
                                <li>名称：{deleteTarget.name}</li>
                            </ul>
                            <p className="text-danger mb-0">この操作は元に戻せません。</p>
                        </>
                    ) : (
                        <p>削除対象が選択されていません。</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            if (!saving) {
                                setShowDeleteModal(false);
                                setDeleteTarget(null);
                            }
                        }}
                    >
                        キャンセル
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleConfirmDelete}
                        disabled={saving || !deleteTarget}
                    >
                        削除する
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* 一括登録モーダル */}
            <Modal
                show={showBulkModal}
                onHide={() => {
                    if (!bulkLoading) {
                        setShowBulkModal(false);
                    }
                }}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>祝日 一括登録</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted">
                        1行ごとに <code>YYYY-MM-DD,名称[,kind]</code> 形式で貼り付けてください。
                        <br />
                        kind を省略した場合は <code>national</code>（祝日）として登録されます。
                        <br />
                        会社祝日は <code>company</code> にしてください。
                    </p>
                    <Form.Group controlId="bulk-import-textarea">
                        <Form.Control
                            as="textarea"
                            rows={10}
                            value={bulkText}
                            onChange={(e) => setBulkText(e.target.value)}
                            placeholder={`例:
2025-01-01,元日
2025-01-13,成人の日
2025-02-11,建国記念の日
2025-02-23,天皇誕生日
2025-02-24,休日
...`}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            if (!bulkLoading) {
                                setShowBulkModal(false);
                            }
                        }}
                    >
                        キャンセル
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleBulkImport}
                        disabled={bulkLoading || !bulkText.trim()}
                    >
                        {bulkLoading ? '登録中…' : '一括登録する'}
                    </Button>
                </Modal.Footer>
            </Modal>

        </Row>
    );
}

export default HolidayClosedDaysPage;
